import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganisation } from '../contexts/OrganisationContext';
import { OrganisationUpdate, Region, RegionCreate, GenerateInstallScriptResponse } from '../types/api';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  GlobeAltIcon,
  PlusIcon,
  ClipboardDocumentIcon,
  LinkIcon,
  ArrowPathIcon,
  SignalIcon,
  SignalSlashIcon,
  CommandLineIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { getApiErrorMessage } from '../constants/messages';
import OrganisationSsoSettings from '../components/Organisation/OrganisationSsoSettings';
import OrganisationMcpSettings from '../components/Organisation/OrganisationMcpSettings';

const OrganisationSettingsPage: React.FC = () => {
  const { t } = (useTranslation as any)('organisations');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const {
    currentOrganisation,
    updateOrganisation,
    deleteOrganisation,
    selectOrganisation,
    canManageSettings,
    currentUserRole,
    canDeleteOrganisation,
  } = useOrganisation();

  const [formData, setFormData] = useState<OrganisationUpdate>({
    nom: '',
  });
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [understandConsequences, setUnderstandConsequences] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Regions state
  const [regions, setRegions] = useState<Region[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [showRegionForm, setShowRegionForm] = useState(false);
  const [newRegionName, setNewRegionName] = useState('');
  const [newRegionDescription, setNewRegionDescription] = useState('');
  const [creatingRegion, setCreatingRegion] = useState(false);

  // Install script flow state
  const [installScript, setInstallScript] = useState<GenerateInstallScriptResponse | null>(null);
  const [installScriptRegionId, setInstallScriptRegionId] = useState<string | null>(null);
  const [generatingScript, setGeneratingScript] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Manual callback form state
  const [showManualForm, setShowManualForm] = useState<string | null>(null); // region ID
  const [manualForm, setManualForm] = useState({
    callback_secret: '',
    tailscale_ip: '',
    bearer_token: '',
    ca_data: '',
    api_port: 6443,
  });
  const [submittingManual, setSubmittingManual] = useState(false);

  useEffect(() => {
    if (deleting) return; // Don't re-fetch during deletion
    if (orgId && (!currentOrganisation || currentOrganisation.id !== orgId)) {
      selectOrganisation(orgId).catch(() => {
        toast.error(t('settings.loadOrgError'));
        navigate('/organisations');
      });
    }
  }, [orgId, currentOrganisation, selectOrganisation, navigate, deleting]);

  useEffect(() => {
    if (currentOrganisation) {
      setFormData({
        nom: currentOrganisation.nom,
      });
    }
  }, [currentOrganisation]);

  // Load regions when org is available
  useEffect(() => {
    if (currentOrganisation?.id) {
      loadRegions();
    }
  }, [currentOrganisation?.id]);

  const loadRegions = async () => {
    if (!currentOrganisation) return;
    setLoadingRegions(true);
    try {
      const data = await apiService.getRegions(currentOrganisation.id, true);
      setRegions(data);
    } catch {
      // Silently fail — regions are optional
    } finally {
      setLoadingRegions(false);
    }
  };

  const handleCreateRegion = async () => {
    if (!currentOrganisation || !newRegionName.trim()) return;

    setCreatingRegion(true);
    try {
      const data: RegionCreate = {
        name: newRegionName.trim(),
        description: newRegionDescription.trim() || undefined,
      };
      await apiService.createRegion(currentOrganisation.id, data);
      toast.success(t('settings.regions.toast.created'));
      setNewRegionName('');
      setNewRegionDescription('');
      setShowRegionForm(false);
      await loadRegions();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('settings.regions.toast.createError'));
    } finally {
      setCreatingRegion(false);
    }
  };

  const handleDeleteRegion = async (regionId: string) => {
    try {
      await apiService.deleteRegion(regionId);
      toast.success(t('settings.regions.toast.deactivated'));
      await loadRegions();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('settings.regions.toast.deleteError'));
    }
  };

  const handleGenerateInstallScript = async (regionId: string) => {
    setGeneratingScript(regionId);
    try {
      const data = await apiService.generateInstallScript(regionId);
      setInstallScript(data);
      setInstallScriptRegionId(regionId);
      setCopied(false);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('settings.regions.toast.scriptError'));
    } finally {
      setGeneratingScript(null);
    }
  };

  const handleCopyCommand = () => {
    if (installScript?.curl_command) {
      navigator.clipboard.writeText(installScript.curl_command);
      setCopied(true);
      toast.success(t('settings.regions.toast.commandCopied'));
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleDisconnectRegion = async (regionId: string) => {
    try {
      await apiService.disconnectRegion(regionId);
      toast.success(t('settings.regions.toast.disconnected'));
      setInstallScript(null);
      setInstallScriptRegionId(null);
      await loadRegions();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('settings.regions.toast.disconnectError'));
    }
  };

  const handleReactivateRegion = async (regionId: string) => {
    try {
      await apiService.reactivateRegion(regionId);
      toast.success(t('settings.regions.toast.reactivated'));
      await loadRegions();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('settings.regions.toast.reactivateError'));
    }
  };

  const handlePermanentDelete = async (regionId: string) => {
    try {
      await apiService.permanentlyDeleteRegion(regionId);
      toast.success(t('settings.regions.toast.permanentlyDeleted'));
      await loadRegions();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('settings.regions.toast.permanentDeleteError'));
    }
  };

  const handleManualCallback = async (regionId: string) => {
    if (!manualForm.callback_secret || !manualForm.tailscale_ip || !manualForm.bearer_token) {
      toast.error(t('settings.regions.manual.requiredFields'));
      return;
    }
    setSubmittingManual(true);
    try {
      await apiService.manualCallback(regionId, manualForm);
      toast.success(t('settings.regions.toast.connected'));
      setShowManualForm(null);
      setManualForm({ callback_secret: '', tailscale_ip: '', bearer_token: '', ca_data: '', api_port: 6443 });
      setInstallScript(null);
      setInstallScriptRegionId(null);
      await loadRegions();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('settings.regions.toast.manualError'));
    } finally {
      setSubmittingManual(false);
    }
  };

  const getConnectionStatusBadge = (region: Region) => {
    if (!region.active) {
      return { color: 'bg-gray-500/12 text-gray-500 line-through', label: t('settings.regions.status.deactivated') };
    }
    const statusMap: Record<string, { color: string; label: string }> = {
      connected: { color: 'bg-green-500/12 text-green-400', label: t('settings.regions.status.connected') },
      provisioning: { color: 'bg-yellow-500/12 text-yellow-400', label: t('settings.regions.status.provisioning') },
      disconnected: { color: 'bg-red-500/12 text-red-400', label: t('settings.regions.status.disconnected') },
      error: { color: 'bg-red-500/12 text-red-400', label: t('settings.regions.status.error') },
      pending: { color: 'bg-gray-500/12 text-gray-400', label: t('settings.regions.status.pending') },
    };
    return statusMap[region.connection_status] || statusMap.pending;
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canManageSettings()) {
      toast.error(t('settings.permissions.noSettings'));
      return;
    }

    if (!formData.nom?.trim()) {
      toast.error(t('settings.permissions.nameRequired'));
      return;
    }

    setSaving(true);
    try {
      await updateOrganisation(currentOrganisation!.id, formData);
      toast.success(t('settings.permissions.updateSuccess'));
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error) || t('settings.permissions.updateError');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDeleteOrganisation()) {
      toast.error(t('settings.permissions.noDelete'));
      return;
    }

    if (deleteConfirmation !== currentOrganisation?.nom) {
      toast.error(t('settings.permissions.confirmMismatch'));
      return;
    }

    if (!understandConsequences) {
      toast.error(t('settings.permissions.mustAcceptConsequences'));
      return;
    }

    setDeleting(true);
    try {
      await deleteOrganisation(currentOrganisation!.id, {
        confirmation_text: deleteConfirmation,
        deletion_reason: deleteReason || undefined,
        understand_consequences: understandConsequences,
      });

      toast.success(t('settings.deleteModal.deleteSuccess'));
      navigate('/organisations');
      // Don't reset deleting — component will unmount after navigate
    } catch (error: unknown) {
      setDeleting(false);
      const errorMessage = getApiErrorMessage(error) || t('settings.permissions.deleteError');
      toast.error(errorMessage);
    }
  };

  if (!currentOrganisation) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-4 text-text-secondary">{t('settings.loading')}</p>
        </div>
      </div>
    );
  }

  if (!canManageSettings()) {
    return (
      <div className="py-8">
        <div className="bg-surface-2 shadow-card rounded-card p-6">
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-yellow-500" />
            <h3 className="mt-4 text-lg font-medium text-text-primary">
              {t('settings.accessDenied.title')}
            </h3>
            <p className="mt-2 text-sm text-text-tertiary">
              {t('settings.accessDenied.description')}
            </p>
            <button
              onClick={() => navigate(`/organisations/${orgId}/dashboard`)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700"
            >
              {t('settings.accessDenied.backButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/organisations/${orgId}/dashboard`)}
            className="text-sm text-accent-400 hover:text-accent-300 mb-2"
          >
            ← {t('settings.backToDashboard')}
          </button>
          <h1 className="text-3xl font-bold text-text-primary">
            {t('settings.title')}
          </h1>
          <p className="mt-2 text-text-secondary">
            {currentOrganisation.nom}
          </p>
        </div>

        {/* General Settings */}
        <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6">
          <h2 className="text-lg font-medium text-text-primary mb-4">
            {t('settings.general.title')}
          </h2>

          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <label htmlFor="nom" className="block text-sm font-medium text-text-secondary">
                {t('settings.general.nameLabel')}
              </label>
              <input
                type="text"
                id="nom"
                value={formData.nom || ''}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                required
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50"
              >
                {saving ? t('settings.general.saving') : t('settings.general.saveButton')}
              </button>
            </div>
          </form>
        </div>

        {/* Regions Section */}
        <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <GlobeAltIcon className="h-5 w-5 text-purple-400" />
              <h2 className="text-lg font-medium text-text-primary">
                {t('settings.regions.title')}
              </h2>
            </div>
            <button
              onClick={() => setShowRegionForm(!showRegionForm)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              {t('settings.regions.newRegion')}
            </button>
          </div>

          <p className="text-sm text-text-tertiary mb-4">
            {t('settings.regions.description')}
          </p>

          {/* Create Region Form */}
          {showRegionForm && (
            <div className="bg-surface-3 rounded-md p-4 mb-4 border border-border-1">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    {t('settings.regions.nameLabel')}
                  </label>
                  <input
                    type="text"
                    value={newRegionName}
                    onChange={(e) => setNewRegionName(e.target.value)}
                    className="block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    placeholder={t('settings.regions.namePlaceholder') as string}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    {t('settings.regions.descriptionLabel')}
                  </label>
                  <input
                    type="text"
                    value={newRegionDescription}
                    onChange={(e) => setNewRegionDescription(e.target.value)}
                    className="block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    placeholder={t('settings.regions.descriptionPlaceholder') as string}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCreateRegion}
                    disabled={creatingRegion || !newRegionName.trim()}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50"
                  >
                    {creatingRegion ? t('settings.regions.creating') : t('settings.regions.createButton')}
                  </button>
                  <button
                    onClick={() => setShowRegionForm(false)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-text-secondary bg-surface-3 border border-border-1 rounded-md hover:bg-surface-4"
                  >
                    {t('cancel', { ns: 'common' })}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Regions List */}
          {loadingRegions ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
            </div>
          ) : regions.length === 0 ? (
            <p className="text-sm text-text-quaternary text-center py-4">
              {t('settings.regions.noRegions')}
            </p>
          ) : (
            <div className="space-y-2">
              {regions.map((region) => {
                const statusBadge = getConnectionStatusBadge(region);
                return (
                  <div key={region.id} className="bg-surface-3 rounded-md border border-border-1">
                    <div className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${
                          region.connection_status === 'connected' ? 'bg-green-500' :
                          region.is_configured ? 'bg-green-500' : 'bg-yellow-500'
                        }`}
                          title={(region.connection_status === 'connected' ? t('settings.regions.tooltips.connectedViaHeadscale') :
                            region.is_configured ? t('settings.regions.tooltips.configured') : t('settings.regions.tooltips.notConfigured')) as string}
                        />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-text-primary">{region.name}</span>
                            <span className="text-xs text-text-tertiary font-mono">{region.slug}</span>
                            {region.is_default && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-500/12 text-blue-400">
                                {t('settings.regions.default')}
                              </span>
                            )}
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                              region.type === 'global'
                                ? 'bg-green-500/12 text-green-400'
                                : 'bg-purple-500/12 text-purple-400'
                            }`}>
                              {region.type}
                            </span>
                            {region.type === 'custom' && (
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${statusBadge.color}`}>
                                {region.connection_status === 'connected' ? (
                                  <SignalIcon className="h-3 w-3" />
                                ) : region.connection_status === 'provisioning' ? (
                                  <ArrowPathIcon className="h-3 w-3 animate-spin" />
                                ) : null}
                                {statusBadge.label}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {region.description && (
                              <p className="text-xs text-text-quaternary">{region.description}</p>
                            )}
                            {region.headscale_ip && (
                              <span className="text-xs text-text-tertiary font-mono">{region.headscale_ip}</span>
                            )}
                          </div>
                          {region.connection_error && (
                            <div className="mt-1">
                              <p className="text-xs text-red-400">
                                {region.connection_error.split('\n---\n')[0]}
                              </p>
                              {region.connection_error.includes('\n---\n') && (
                                <div className="mt-2 relative">
                                  <pre className="bg-surface-0 border border-border-1 rounded-md p-2 text-xs font-mono text-text-secondary overflow-x-auto whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                                    {region.connection_error.split('\n---\n')[1]}
                                  </pre>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(region.connection_error!.split('\n---\n')[1]);
                                      toast.success(t('settings.regions.actions.copiedManifest'));
                                    }}
                                    className="absolute top-1 right-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-surface-3 border border-border-1 text-text-secondary hover:text-text-primary hover:bg-surface-4 transition-colors"
                                  >
                                    <ClipboardDocumentIcon className="h-3 w-3" />
                                    {t('settings.regions.actions.copy')}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Active region actions */}
                        {region.type === 'custom' && region.active && (
                          <>
                            {region.connection_status !== 'connected' && (
                              <button
                                onClick={() => handleGenerateInstallScript(region.id)}
                                disabled={generatingScript === region.id}
                                className="inline-flex items-center gap-1 text-xs font-medium text-purple-400 hover:text-purple-300 px-2 py-1 rounded hover:bg-purple-500/12 disabled:opacity-50"
                              >
                                {generatingScript === region.id ? (
                                  <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <LinkIcon className="h-3.5 w-3.5" />
                                )}
                                {region.connection_status === 'pending' ? t('settings.regions.actions.connect') :
                                 region.connection_status === 'provisioning' ? t('settings.regions.actions.regenerate') : t('settings.regions.actions.reconnect')}
                              </button>
                            )}
                            {region.connection_status === 'connected' && (
                              <button
                                onClick={() => handleDisconnectRegion(region.id)}
                                className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-500/12"
                              >
                                <SignalSlashIcon className="h-3.5 w-3.5" />
                                {t('settings.regions.actions.disconnect')}
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteRegion(region.id)}
                              className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-500/12"
                            >
                              {t('settings.regions.actions.deactivate')}
                            </button>
                          </>
                        )}
                        {/* Inactive region actions */}
                        {region.type === 'custom' && !region.active && (
                          <>
                            <button
                              onClick={() => handleReactivateRegion(region.id)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-green-400 hover:text-green-300 px-2 py-1 rounded hover:bg-green-500/12"
                            >
                              <ArrowPathIcon className="h-3.5 w-3.5" />
                              {t('settings.regions.actions.reactivate')}
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(t('settings.regions.actions.deleteConfirm') as string)) {
                                  handlePermanentDelete(region.id);
                                }
                              }}
                              className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-500/12"
                            >
                              {t('settings.regions.actions.delete')}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Install script panel */}
                    {installScriptRegionId === region.id && installScript && (
                      <div className="border-t border-border-1 p-4 bg-surface-2 rounded-b-md">
                        <div className="flex items-center gap-2 mb-3">
                          <CommandLineIcon className="h-4 w-4 text-purple-400" />
                          <span className="text-sm font-medium text-text-primary">
                            {t('settings.regions.installScript.title')}
                          </span>
                          <span className="text-xs text-text-quaternary">
                            {t('settings.regions.installScript.validUntil', { date: new Date(installScript.expires_at).toLocaleString() })}
                          </span>
                        </div>
                        <div className="flex items-start gap-2 p-2 mb-3 bg-yellow-500/8 border border-yellow-500/20 rounded-md">
                          <ExclamationTriangleIcon className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-yellow-300" dangerouslySetInnerHTML={{ __html: t('settings.regions.installScript.warning', { date: new Date(installScript.script_valid_until).toLocaleString() }) as string }} />
                        </div>
                        <p className="text-xs text-text-tertiary mb-1">
                          {t('settings.regions.installScript.runAsRoot')}
                        </p>
                        <p className="text-xs text-text-quaternary mb-3" dangerouslySetInnerHTML={{ __html: t('settings.regions.installScript.noPublicIp') as string }} />
                        <div className="relative">
                          <pre className="bg-surface-0 border border-border-1 rounded-md p-3 text-xs font-mono text-text-secondary overflow-x-auto whitespace-pre-wrap break-all">
                            {installScript.curl_command}
                          </pre>
                          <button
                            onClick={handleCopyCommand}
                            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-surface-3 border border-border-1 text-text-secondary hover:text-text-primary hover:bg-surface-4 transition-colors"
                          >
                            {copied ? (
                              <>
                                <CheckCircleIcon className="h-3.5 w-3.5 text-green-400" />
                                {t('settings.regions.actions.copied')}
                              </>
                            ) : (
                              <>
                                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                                {t('settings.regions.actions.copy')}
                              </>
                            )}
                          </button>
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <p className="text-xs text-text-quaternary">
                            {t('settings.regions.installScript.callbackFailed')}
                          </p>
                          <button
                            onClick={() => setShowManualForm(showManualForm === region.id ? null : region.id)}
                            className="text-xs text-purple-400 hover:text-purple-300 underline"
                          >
                            {showManualForm === region.id ? t('settings.regions.installScript.hide') : t('settings.regions.installScript.manualConnection')}
                          </button>
                        </div>

                        {/* Manual callback form */}
                        {showManualForm === region.id && (
                          <div className="mt-3 p-3 bg-surface-3 rounded-md border border-border-1 space-y-2">
                            <p className="text-xs text-text-secondary font-medium mb-2">
                              {t('settings.regions.manual.instructions')}
                            </p>
                            <input
                              type="text"
                              placeholder="Tailscale IP"
                              value={manualForm.tailscale_ip}
                              onChange={(e) => setManualForm({ ...manualForm, tailscale_ip: e.target.value })}
                              className="block w-full border border-border-1 rounded-md py-1.5 px-2.5 bg-surface-3 text-text-primary text-xs focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                            />
                            <input
                              type="text"
                              placeholder="Bearer Token"
                              value={manualForm.bearer_token}
                              onChange={(e) => setManualForm({ ...manualForm, bearer_token: e.target.value })}
                              className="block w-full border border-border-1 rounded-md py-1.5 px-2.5 bg-surface-3 text-text-primary text-xs font-mono focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                            />
                            <input
                              type="text"
                              placeholder="CA Data (optionnel)"
                              value={manualForm.ca_data}
                              onChange={(e) => setManualForm({ ...manualForm, ca_data: e.target.value })}
                              className="block w-full border border-border-1 rounded-md py-1.5 px-2.5 bg-surface-3 text-text-primary text-xs font-mono focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                            />
                            <input
                              type="text"
                              placeholder="Callback Secret"
                              value={manualForm.callback_secret}
                              onChange={(e) => setManualForm({ ...manualForm, callback_secret: e.target.value })}
                              className="block w-full border border-border-1 rounded-md py-1.5 px-2.5 bg-surface-3 text-text-primary text-xs font-mono focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                            />
                            <div className="flex gap-2 pt-1">
                              <button
                                onClick={() => handleManualCallback(region.id)}
                                disabled={submittingManual || !manualForm.tailscale_ip || !manualForm.bearer_token || !manualForm.callback_secret}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50"
                              >
                                {submittingManual ? t('settings.regions.manual.connecting') : t('settings.regions.manual.connectButton')}
                              </button>
                              <button
                                onClick={() => setShowManualForm(null)}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-text-secondary bg-surface-3 border border-border-1 rounded-md hover:bg-surface-4"
                              >
                                {t('cancel', { ns: 'common' })}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Connexion SSO : fournisseur Entra ID, domaines verifies, politique de connexion */}
        <OrganisationSsoSettings organisationId={currentOrganisation.id} isOwner={currentUserRole === 'owner'} />
        <OrganisationMcpSettings organisationId={currentOrganisation.id} isAdmin={currentUserRole === 'owner' || currentUserRole === 'admin'} />

        {/* Danger Zone */}
        {canDeleteOrganisation() && (
          <div className="bg-surface-2 shadow-card rounded-card p-6 border-2 border-red-500/20">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mt-1 mr-3" />
              <div className="flex-1">
                <h2 className="text-lg font-medium text-red-400 mb-2">
                  {t('settings.dangerZone.title')}
                </h2>
                <p className="text-sm text-text-secondary mb-4">
                  {t('settings.dangerZone.description')}
                </p>

                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-red-500/20 rounded-md shadow-sm text-sm font-medium text-red-400 bg-surface-2 hover:bg-red-500/12 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  {t('settings.dangerZone.deleteButton')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={() => setShowDeleteModal(false)}></div>

            <div className="inline-block align-bottom bg-surface-2 rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="bg-surface-2 rounded-md text-text-quaternary hover:text-text-tertiary focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-500/12 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-text-primary mb-4">
                    {t('settings.deleteModal.title')}
                  </h3>

                  <div className="space-y-4">
                    <div className="bg-red-500/12 border border-red-500/20 rounded-md p-4">
                      <p className="text-sm text-red-400 font-medium mb-2">
                        <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" /> {t('settings.deleteModal.irreversible')}
                      </p>
                      <ul className="text-sm text-red-400 list-disc list-inside space-y-1">
                        <li>{t('settings.deleteModal.allProjectsDeleted')}</li>
                        <li>{t('settings.deleteModal.allEnvsDeleted')}</li>
                        <li>{t('settings.deleteModal.allAppsDeleted')}</li>
                        <li>{t('settings.deleteModal.dataRetention')}</li>
                      </ul>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2" dangerouslySetInnerHTML={{ __html: t('settings.deleteModal.confirmLabel', { name: currentOrganisation.nom }) as string }} />
                      <input
                        type="text"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        className="block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-red-500 focus:border-red-500"
                        placeholder={currentOrganisation.nom}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t('settings.deleteModal.reasonLabel')}
                      </label>
                      <textarea
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        rows={3}
                        className="block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-red-500 focus:border-red-500"
                        placeholder={t('settings.deleteModal.reasonPlaceholder') as string}
                      />
                    </div>

                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="understand"
                        checked={understandConsequences}
                        onChange={(e) => setUnderstandConsequences(e.target.checked)}
                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-border-1 rounded mt-1"
                      />
                      <label htmlFor="understand" className="ml-2 block text-sm text-text-secondary">
                        {t('settings.deleteModal.understandCheckbox')}
                      </label>
                    </div>
                  </div>

                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      disabled={deleting || deleteConfirmation !== currentOrganisation.nom || !understandConsequences}
                      onClick={handleDelete}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? t('settings.deleteModal.deleting') : t('settings.deleteModal.deleteForever')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteModal(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-border-1 shadow-sm px-4 py-2 bg-surface-3 text-base font-medium text-text-secondary hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      {t('cancel', { ns: 'common' })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganisationSettingsPage;
